import React, { useState } from 'react';
import type { GenerationVariant } from './useGenerationJob';

/**
 * Shared AI-variant picker grid + "More like this" popover (extracted
 * verbatim from BannerEditor's generate mode, 2026-09-09). Markup/classes
 * are byte-identical to the banner original under default props; LogoEditor
 * passes `imageWrapClassName` for its square, checkered-transparency wrap.
 */

interface VariantGridProps {
  variants: GenerationVariant[];
  selectedUrl: string | null;
  onSelect: (url: string) => void;
  onMoreLikeThis: (variantUrl: string, overrides: { addendumExtra?: string; styleOverride?: string }) => void;
  /** jobState waiting|active — disables the More-like-this buttons. */
  busy: boolean;
  narrow?: boolean;
  styleOptions: Array<{ key: string; name: string }>;
  defaultStyleKey: string;
  /** Wrapper class around each <img>; banner default is a white rounded box. */
  imageWrapClassName?: string;
}

export default function VariantGrid({
  variants,
  selectedUrl,
  onSelect,
  onMoreLikeThis,
  busy,
  narrow = false,
  styleOptions,
  defaultStyleKey,
  imageWrapClassName = 'overflow-hidden rounded-xl bg-white',
}: VariantGridProps) {
  const [morePopoverIdx, setMorePopoverIdx] = useState<number | null>(null);
  const [mltExtraAddendum, setMltExtraAddendum] = useState<string>('');
  const [mltStyleOverride, setMltStyleOverride] = useState<string>('');

  const openMorePopover = (idx: number) => {
    setMorePopoverIdx(idx);
    setMltExtraAddendum('');
    setMltStyleOverride(defaultStyleKey);
  };

  const closeMorePopover = () => setMorePopoverIdx(null);

  const submitMorePopover = (variantUrl: string) => {
    const overrides: { addendumExtra?: string; styleOverride?: string } = {};
    if (mltStyleOverride && mltStyleOverride !== defaultStyleKey) {
      overrides.styleOverride = mltStyleOverride;
    }
    if (mltExtraAddendum.trim()) {
      overrides.addendumExtra = mltExtraAddendum.trim();
    }
    closeMorePopover();
    onMoreLikeThis(variantUrl, overrides);
  };

  if (variants.length === 0) return null;

  return (
    <div className={narrow ? 'grid gap-4 grid-cols-1' : 'grid gap-4 sm:grid-cols-2 lg:grid-cols-3'}>
      {variants.map((variant, idx) => {
        const isSelected = selectedUrl === variant.url;
        return (
          <div
            key={`variant-${idx}`}
            className={`group relative overflow-hidden rounded-2xl p-3 transition-all duration-200 ${
              isSelected
                ? 'bg-foreground shadow-lg shadow-slate-900/20 ring-2 ring-foreground'
                : 'bg-muted/50 ring-1 ring-border hover:ring-input'
            }`}
          >
            <button
              type="button"
              onClick={() => onSelect(variant.url)}
              className="block w-full text-left"
            >
              <div className={imageWrapClassName}>
                <img src={variant.url} alt={`Variant ${idx + 1}`} className="h-auto w-full object-contain" />
              </div>
              <div className={`mt-2 text-center text-sm font-medium ${isSelected ? 'text-white' : 'text-muted-foreground'}`}>
                Variant {idx + 1}
              </div>
            </button>
            <button
              type="button"
              onClick={() => openMorePopover(idx)}
              disabled={busy}
              className={`mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                isSelected
                  ? 'bg-white/10 text-white hover:bg-white/20'
                  : 'text-primary hover:bg-primary/10'
              }`}
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              More like this
            </button>

            {morePopoverIdx === idx && (
              <div className="mt-3 rounded-xl bg-card p-3 ring-1 ring-border shadow-lg">
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-foreground">What to change</span>
                  <textarea
                    rows={2}
                    value={mltExtraAddendum}
                    onChange={(e) => setMltExtraAddendum(e.target.value.slice(0, 200))}
                    placeholder="e.g. make it darker, add a sunset gradient"
                    className="w-full rounded-lg border-0 bg-muted/50 px-3 py-2 text-xs text-foreground ring-1 ring-border placeholder:text-muted-foreground/60 focus:bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </label>
                <label className="mt-2 block">
                  <span className="mb-1 block text-xs font-medium text-foreground">Change style to</span>
                  <select
                    value={mltStyleOverride}
                    onChange={(e) => setMltStyleOverride(e.target.value)}
                    className="w-full rounded-lg border-0 bg-muted/50 px-3 py-2 text-xs text-foreground ring-1 ring-border focus:bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {styleOptions.map((s) => (
                      <option key={`mlt-style-${s.key}`} value={s.key}>{s.name}</option>
                    ))}
                  </select>
                </label>
                <div className="mt-3 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={closeMorePopover}
                    className="rounded-lg px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => submitMorePopover(variant.url)}
                    className="rounded-lg bg-foreground px-3 py-1.5 text-xs font-semibold text-white hover:bg-foreground/90"
                  >
                    Generate
                  </button>
                </div>
              </div>
            )}

            {isSelected && (
              <div className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-success shadow-sm">
                <svg className="h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
